import axios from "axios";
import { toast } from "react-toastify";

export const api = axios.create({
  baseURL: import.meta.env.PUBLIC_API_BASE_URL,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

export const authApi = axios.create({
  baseURL: import.meta.env.PUBLIC_AUTH_BASE_URL,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

/* INTERNAL STATE */
let isToastShown = false;

// Track interceptors per instance
const interceptorMap = new Map();

/* HELPERS */
const showError = (msg) => {
  if (!isToastShown) {
    toast.error(msg);
    isToastShown = true;
    setTimeout(() => (isToastShown = false), 2000);
  }
};

/* INTERCEPTORS */

// Request → attach token
const attachAuthInterceptor = (instance, store) => {
  return instance.interceptors.request.use(
    (config) => {
      const token = store?.getToken?.();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );
};

// Response → handle refresh + errors
const attachResponseInterceptor = (instance, store) => {
  return instance.interceptors.response.use(
    (res) => res,
    async (error) => {
      const originalRequest = error?.config;
      const status = error?.response?.status;

      // Network error
      if (!error.response) {
        showError("Network error. Please check your connection.");
        return Promise.reject(error);
      }

      // Handle 401 — attempt silent refresh, then retry the original request.
      // On refresh failure, fall back to logout.
      //
      // EXCEPTION: if the SessionProvider has the refresh modal open
      // (window.__cfSessionRefreshModalOpen === true), the user is
      // already being warned about the imminent expiry via the UI. Do
      // NOT preempt their decision with a refresh attempt or a logout.
      // Just reject the request — the user will click Extend in the
      // modal to refresh, and any data they need can be re-fetched
      // after the modal closes. This prevents the 1-second-logout
      // race where an in-flight API call returns 401 at the exact
      // moment the modal opens, the interceptor's refresh fails for
      // some reason, and the user is hard-redirected before they can
      // click Extend.
      if (status === 401 && originalRequest && !originalRequest._retry) {
        if (typeof window !== "undefined" && window.__cfSessionRefreshModalOpen) {
          // User is being warned via the modal. Don't race their
          // decision. Reject the request; modal flow will handle it.
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        const refresher = store?.refreshToken;
        if (typeof refresher === "function") {
          try {
            const newToken = await refresher();
            if (newToken) {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return instance(originalRequest);
            }
          } catch {
            // refresh failed — fall through to logout
          }
        }

        // No refresher or refresh failed — force logout.
        store?.logout?.();
        if (typeof window !== "undefined") {
          window.location.href = window.location.origin;
        }
        return Promise.reject(error);
      }

      // Global error handling
      switch (status) {
        case 400:
          showError(error?.response?.data?.message || "Bad Request");
          break;

        case 403:
          showError(error?.response?.data?.message || "You are not authorized");
          break;

        case 404:
          showError(
            error?.response?.data?.message || "Requested resource not found",
          );
          break;

        case 409:
          showError(error?.response?.data?.message || "Conflict occurred");
          break;

        case 422:
          showError(error?.response?.data?.message || "Validation error");
          break;

        case 500:
          showError(
            error?.response?.data?.message ||
              "Server error. Please try again later",
          );
          break;

        default:
          showError("Something went wrong");
      }

      return Promise.reject(error);
    },
  );
};

export const setupInterceptors = (instances = [], store = {}) => {
  instances.forEach((instance) => {
    const existing = interceptorMap.get(instance);

    if (existing) {
      instance.interceptors.request.eject(existing.request);
      instance.interceptors.response.eject(existing.response);
    }

    const requestId = attachAuthInterceptor(instance, store);
    const responseId = attachResponseInterceptor(instance, store);

    interceptorMap.set(instance, {
      request: requestId,
      response: responseId,
    });
  });
};
